import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getBusinessInquiries } from '../../../api/franchise.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IBusinessInquiryItem,
  IGetBusinessInquiries,
} from '../../../models/franchise.model';
import { RootState } from '../../../store/rootReducer';

const BusinessInquiry = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0013 = 가맹문의 상태
  const BR0013 = commonCodes.find(
    (item) => item.commonCode === 'BR0013',
  )?.detailCode;
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetBusinessInquiries>();

  const [answerDiv, setAnswerDiv] = useState<string>();
  const [field, setField] = useState('representName');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.answerDiv) {
      _param.answerDiv = String(query.answerDiv);
      setAnswerDiv(_param.answerDiv);
    }
    if (query.representName) {
      _param.representName = String(query.representName);
      setField('representName');
      setKeyword(_param.representName);
    }
    if (query.mobilePhone) {
      _param.mobilePhone = String(query.mobilePhone);
      setField('mobilePhone');
      setKeyword(_param.mobilePhone);
    }

    execute(getBusinessInquiries(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (answerDiv) {
      param.answerDiv = answerDiv;
    }

    if (field && keyword) {
      switch (field) {
        case 'representName':
          param.representName = keyword;
          break;
        case 'mobilePhone':
          param.mobilePhone = keyword;
          break;
      }  
    }

    history.push(`/business/inquiry?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IBusinessInquiryItem> = [
    {
      title: t('business.inquiry.column1'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('business.inquiry.column2'),
      dataIndex: 'representName',
      key: 'representName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.representName?.localeCompare(b.representName),
    },
    {
      title: t('business.inquiry.column3'),
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.mobilePhone?.localeCompare(b.mobilePhone),
    },
    {
      title: t('business.inquiry.column4'),
      dataIndex: 'email',
      key: 'email',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.email?.localeCompare(b.email),
    },
    {
      title: t('business.inquiry.column5'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      sorter: (a, b) => a.content?.localeCompare(b.content),
      render: (value) => <div className="text-left ant-table-cell-ellipsis">{value}</div>
    },
    {
      title: t('business.inquiry.column6'),
      dataIndex: 'answerDiv',
      key: 'answerDiv',
      width: '10%',
      sorter: (a, b) => a.answerDiv?.localeCompare(b.answerDiv),
      render: (value, record) => {
        const status = getCommonCodeLabel('BR0013', value, 'ko');
        const date = record.answerDateTime
          ? ` (${moment(record.answerDateTime).format('YYMMDD')})`
          : '';
        return status + date;
      },
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-business-big.svg"
      parentMenu={t('common.menu.business')}
      currentMenu={t('business.inquiry.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('business.inquiry.column6')}</label>
            <div>
              <Select
                placeholder={t('business.inquiry.column6')}
                value={answerDiv}
                onChange={(value) => setAnswerDiv(value)}
              >
                {BR0013?.map((item) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <div>
              <Select
                placeholder={t('common.button.select')}
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="representName">
                  {t('business.inquiry.column2')}
                </Select.Option>
                <Select.Option value="mobilePhone">
                  {t('business.inquiry.column3')}
                </Select.Option>
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-150p"
              placeholder={t('common.text.input')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.data?.length || 0}</b>{t('common.text.unit')}</div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/business/inquiry/${record.inquiryCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName={(record) =>
              'pointer' + (record.answerDiv === 'FI' ? ' chosen' : '')
            }
            rowKey="inquiryCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/business/inquiry"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default BusinessInquiry;
